/**
 * Server functions for linking shelf codes to non-shelf fixture types.
 *
 * Each link causes the shelf code's article list to contribute to the LoM
 * for every placed fixture of `shelveType`. See `bomComputation.ts`.
 */
import { createServerFn } from "@tanstack/react-start"
import { getRequest } from "@tanstack/react-start/server"
import { and, asc, eq } from "drizzle-orm"
import { z } from "zod"
import type { SchemaShelveType } from "@/openapi-schema"
import { fixtureTypeShelfCodes, shelfCodeDefinitions } from "@/db/bom-schema"
import { db } from "@/db/db"
import { logAuditEvent } from "@/features/audit-logs/server/auditLogger"
import { assertPermission } from "@/lib/assert-permission.server"
import { authFunctionMiddleware } from "@/lib/auth-middleware"
import {
  ANY_STORE,
  describeStoreConditions,
  sameStoreConditions,
  type StoreConditions,
  StoreConditionsSchema,
} from "../core/storeConditions"
import { type DbOrTransaction, lockConfigVersion } from "./materialRulesDb.server"
import { assertStoreConditionsValid } from "./storeConditionsDb.server"

const ShelveTypeSchema = z.string().min(1)

// ─────────────────────────────────────────────────────────────────────────────
// List (returns links + denormalized shelf code metadata for display)
// ─────────────────────────────────────────────────────────────────────────────

export const listFixtureTypeShelfCodesFn = createServerFn({ method: "GET" })
  .middleware([authFunctionMiddleware])
  .inputValidator(z.object({ configVersionId: z.string() }))
  .handler(async ({ data }) => {
    await assertPermission(getRequest(), "configVersion", "read")
    const rows = await db
      .select({
        id: fixtureTypeShelfCodes.id,
        shelveType: fixtureTypeShelfCodes.shelveType,
        shelfCodeId: fixtureTypeShelfCodes.shelfCodeId,
        sortOrder: fixtureTypeShelfCodes.sortOrder,
        storeVersion: fixtureTypeShelfCodes.storeVersion,
        countryId: fixtureTypeShelfCodes.countryId,
        regionCode: fixtureTypeShelfCodes.regionCode,
        storeSizes: fixtureTypeShelfCodes.storeSizes,
        shelfCode: shelfCodeDefinitions.code,
        shelfCodeDisplayName: shelfCodeDefinitions.displayName,
      })
      .from(fixtureTypeShelfCodes)
      .innerJoin(
        shelfCodeDefinitions,
        eq(fixtureTypeShelfCodes.shelfCodeId, shelfCodeDefinitions.id),
      )
      .where(eq(fixtureTypeShelfCodes.configVersionId, data.configVersionId))
      .orderBy(asc(fixtureTypeShelfCodes.shelveType), asc(fixtureTypeShelfCodes.sortOrder))

    return rows
  })

// ─────────────────────────────────────────────────────────────────────────────
// Create
// ─────────────────────────────────────────────────────────────────────────────

const CreateFixtureTypeShelfCodeSchema = z.object({
  configVersionId: z.string(),
  shelveType: ShelveTypeSchema,
  shelfCodeId: z.string(),
  sortOrder: z.number().int().default(0),
  conditions: StoreConditionsSchema.default(ANY_STORE),
})

/** The same code for the same fixture and stores only once; other stores can differ (BLU-782). */
async function assertNotLinkedTwice(
  tx: DbOrTransaction,
  link: { configVersionId: string; shelveType: string; shelfCodeId: string },
  code: string,
  conditions: StoreConditions,
  ownId: string | null,
) {
  const rows = await tx.query.fixtureTypeShelfCodes.findMany({
    where: and(
      eq(fixtureTypeShelfCodes.configVersionId, link.configVersionId),
      eq(fixtureTypeShelfCodes.shelveType, link.shelveType as SchemaShelveType),
      eq(fixtureTypeShelfCodes.shelfCodeId, link.shelfCodeId),
    ),
  })
  if (rows.some((r) => r.id !== ownId && sameStoreConditions(r, conditions))) {
    throw new Response(
      `${code} is already linked to this fixture for ${describeStoreConditions(conditions)}; change that link, or pick other stores`,
      { status: 400 },
    )
  }
}

export const createFixtureTypeShelfCodeFn = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .inputValidator(CreateFixtureTypeShelfCodeSchema)
  .handler(async ({ data }) => {
    const request = getRequest()
    await assertPermission(request, "configVersion", "update")
    await assertStoreConditionsValid(data.conditions)

    const shelfCode = await db.query.shelfCodeDefinitions.findFirst({
      where: eq(shelfCodeDefinitions.id, data.shelfCodeId),
    })
    if (!shelfCode || shelfCode.configVersionId !== data.configVersionId) {
      throw new Response("Shelf code does not belong to this configuration version", {
        status: 400,
      })
    }
    const created = await db.transaction(async (tx) => {
      await lockConfigVersion(tx, data.configVersionId)
      const stillThere = await tx.query.shelfCodeDefinitions.findFirst({
        where: eq(shelfCodeDefinitions.id, data.shelfCodeId),
      })
      if (!stillThere) throw new Response(`${shelfCode.code} was just removed`, { status: 409 })
      await assertNotLinkedTwice(tx, data, shelfCode.code, data.conditions, null)
      const [row] = await tx
        .insert(fixtureTypeShelfCodes)
        .values({
          configVersionId: data.configVersionId,
          shelveType: data.shelveType as SchemaShelveType,
          shelfCodeId: data.shelfCodeId,
          sortOrder: data.sortOrder,
          ...data.conditions,
        })
        .returning()
      return row
    })

    await logAuditEvent(
      {
        action: "fixtureTypeShelfCode.create",
        entityType: "fixtureTypeShelfCode",
        entityId: created.id,
        entityName: `${data.shelveType}/${shelfCode.code}`,
        summary: `Linked shelf code "${shelfCode.code}" to fixture type "${data.shelveType}" for ${describeStoreConditions(data.conditions)}`,
        metadata: { configVersionId: data.configVersionId },
      },
      request,
    )

    return created
  })

// ─────────────────────────────────────────────────────────────────────────────
// Delete
// ─────────────────────────────────────────────────────────────────────────────

export const deleteFixtureTypeShelfCodeFn = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .inputValidator(z.object({ id: z.string() }))
  .handler(async ({ data }) => {
    const request = getRequest()
    await assertPermission(request, "configVersion", "update")

    const existing = await db.query.fixtureTypeShelfCodes.findFirst({
      where: eq(fixtureTypeShelfCodes.id, data.id),
    })
    if (!existing) throw new Response("Link not found", { status: 404 })

    const shelfCode = await db.query.shelfCodeDefinitions.findFirst({
      where: eq(shelfCodeDefinitions.id, existing.shelfCodeId),
    })
    await db.delete(fixtureTypeShelfCodes).where(eq(fixtureTypeShelfCodes.id, data.id))

    const code = shelfCode?.code ?? existing.shelfCodeId
    await logAuditEvent(
      {
        action: "fixtureTypeShelfCode.delete",
        entityType: "fixtureTypeShelfCode",
        entityId: data.id,
        entityName: `${existing.shelveType}/${code}`,
        summary: `Unlinked shelf code "${code}" from fixture type "${existing.shelveType}"`,
        metadata: { configVersionId: existing.configVersionId },
      },
      request,
    )

    return { success: true }
  })

// ─────────────────────────────────────────────────────────────────────────────
// Change which stores a link is for (BLU-782)
// ─────────────────────────────────────────────────────────────────────────────

export const updateFixtureTypeShelfCodeFn = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .inputValidator(z.object({ id: z.string(), conditions: StoreConditionsSchema }))
  .handler(async ({ data }) => {
    const request = getRequest()
    await assertPermission(request, "configVersion", "update")
    await assertStoreConditionsValid(data.conditions)

    const found = await db.query.fixtureTypeShelfCodes.findFirst({
      where: eq(fixtureTypeShelfCodes.id, data.id),
    })
    if (!found) throw new Response("Link not found", { status: 404 })
    const { before, code } = await db.transaction(async (tx) => {
      await lockConfigVersion(tx, found.configVersionId)
      // Read again under the lock: the link may have changed or gone meanwhile.
      const link = await tx.query.fixtureTypeShelfCodes.findFirst({
        where: eq(fixtureTypeShelfCodes.id, data.id),
      })
      if (!link) throw new Response("Link not found", { status: 404 })
      const shelfCode = await tx.query.shelfCodeDefinitions.findFirst({
        where: eq(shelfCodeDefinitions.id, link.shelfCodeId),
      })
      const name = shelfCode?.code ?? link.shelfCodeId
      await assertNotLinkedTwice(tx, link, name, data.conditions, link.id)
      await tx
        .update(fixtureTypeShelfCodes)
        .set(data.conditions)
        .where(eq(fixtureTypeShelfCodes.id, link.id))
      return { before: link, code: name }
    })

    await logAuditEvent(
      {
        action: "fixtureTypeShelfCode.update",
        entityType: "fixtureTypeShelfCode",
        entityId: data.id,
        entityName: `${before.shelveType}/${code}`,
        summary: `Shelf code "${code}" on fixture type "${before.shelveType}" now for ${describeStoreConditions(data.conditions)} (was ${describeStoreConditions(before)})`,
        metadata: { configVersionId: before.configVersionId },
      },
      request,
    )
    return { success: true }
  })
